<script>
  import AdminNav from '@/components/admin/AdminNav.svelte';
  import { router } from '@inertiajs/svelte'

  let { organizations = [], pagination = {}, filters = {}, stats = {}, flash = {} } = $props()
  const getInitialFilters = () => filters

  let typeFilter = $state(getInitialFilters().type || '')
  let verificationEnabledFilter = $state(getInitialFilters().verification_enabled || '')
  let loginAllowedFilter = $state(getInitialFilters().login_allowed || '')

  function applyFilters() {
    const params = {}
    if (typeFilter) params.type = typeFilter
    if (verificationEnabledFilter !== '') params.verification_enabled = verificationEnabledFilter
    if (loginAllowedFilter !== '') params.login_allowed = loginAllowedFilter
    router.get('/admin/organizations', params, { preserveState: true })
  }

  function goToPage(page) {
    router.get('/admin/organizations', { ...filters, page }, { preserveState: true })
  }

  function getTypeBadge(type) {
    switch (type) {
      case 'public': return 'badge-info'
      case 'private': return 'badge-primary'
      case 'financial': return 'badge-success'
      case 'government': return 'badge-error'
      default: return 'badge-neutral'
    }
  }

  function getTypeLabel(type) {
    const labels = {
      public: '공기업',
      private: '민간기업',
      financial: '금융기관',
      government: '정부기관'
    }
    return labels[type] || type
  }

  function toggleVerification(org) {
    const actionLabel = org.verification_enabled ? '비활성화' : '활성화'
    if (confirm(`"${org.name}" 도메인의 인증 사용을 ${actionLabel}하시겠습니까?`)) {
      router.post(`/admin/organizations/${org.id}/toggle_verification`)
    }
  }

  function toggleLoginAccess(org) {
    const actionLabel = org.login_allowed ? '제한' : '허용'
    if (confirm(`"${org.name}" 도메인 사용자의 서비스 이용을 ${actionLabel}하시겠습니까?`)) {
      router.post(`/admin/organizations/${org.id}/toggle_login_access`)
    }
  }
</script>

<AdminNav activeTab="organizations" />

  <main>
    {#if flash.notice}
      <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}

    <div class="flex items-center justify-between mb-6">
      <h2 class="text-h1 text-content">기관/회사 관리</h2>
      <a href="/admin/organizations/new" class="btn btn-primary btn-md">+ 기관 추가</a>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 md:grid-cols-7 gap-4 mb-6">
      <div class="card card-bordered p-4 shadow-sm text-center">
        <div class="text-h2 text-content">{stats.total || 0}</div>
        <div class="text-caption text-content-tertiary">전체</div>
      </div>
      <div class="card card-bordered p-4 shadow-sm text-center">
        <div class="text-h2 text-blue-600">{stats.government || 0}</div>
        <div class="text-caption text-content-tertiary">정부기관</div>
      </div>
      <div class="card card-bordered p-4 shadow-sm text-center">
        <div class="text-h2 text-cyan-600">{stats.public || 0}</div>
        <div class="text-caption text-content-tertiary">공기업</div>
      </div>
      <div class="card card-bordered p-4 shadow-sm text-center">
        <div class="text-h2 text-purple-600">{stats.private || 0}</div>
        <div class="text-caption text-content-tertiary">민간기업</div>
      </div>
      <div class="card card-bordered p-4 shadow-sm text-center">
        <div class="text-h2 text-green-600">{stats.financial || 0}</div>
        <div class="text-caption text-content-tertiary">금융기관</div>
      </div>
      <div class="card card-bordered p-4 shadow-sm text-center">
        <div class="text-h2 text-success">{stats.verification_enabled || 0}</div>
        <div class="text-caption text-content-tertiary">인증 사용</div>
      </div>
      <div class="card card-bordered p-4 shadow-sm text-center">
        <div class="text-h2 text-error">{stats.login_blocked || 0}</div>
        <div class="text-caption text-content-tertiary">이용 제한</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card card-bordered p-4 mb-6 shadow-md">
      <div class="flex flex-wrap items-center gap-4">
        <select bind:value={typeFilter} class="select">
          <option value="">모든 유형</option>
          <option value="government">정부기관</option>
          <option value="public">공기업</option>
          <option value="private">민간기업</option>
          <option value="financial">금융기관</option>
        </select>
        <select bind:value={verificationEnabledFilter} class="select">
          <option value="">인증 설정 전체</option>
          <option value="true">도메인 인증 사용</option>
          <option value="false">도메인 인증 미사용</option>
        </select>
        <select bind:value={loginAllowedFilter} class="select">
          <option value="">이용 상태 전체</option>
          <option value="true">이용 허용</option>
          <option value="false">이용 제한</option>
        </select>
        <button onclick={applyFilters} class="btn btn-primary btn-md">검색</button>
      </div>
    </div>

    <!-- Organizations Table -->
    <div class="card card-bordered overflow-hidden shadow-md">
      <table class="w-full">
        <thead class="bg-surface-tertiary">
          <tr>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">기관명</th>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">도메인</th>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">유형</th>
            <th class="px-6 py-3 text-center text-caption font-semibold text-content-tertiary uppercase">직원 수</th>
            <th class="px-6 py-3 text-center text-caption font-semibold text-content-tertiary uppercase">자동 인증</th>
            <th class="px-6 py-3 text-center text-caption font-semibold text-content-tertiary uppercase">도메인 인증</th>
            <th class="px-6 py-3 text-center text-caption font-semibold text-content-tertiary uppercase">이용 상태</th>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">등록일</th>
            <th class="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each organizations as org}
            <tr class="hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors">
              <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                  {#if org.logo_url}
                    <img src={org.logo_url} alt="" class="w-8 h-8 rounded-lg object-cover" />
                  {:else}
                    <div class="w-8 h-8 bg-primary-50 dark:bg-dark-tertiary rounded-lg flex items-center justify-center text-primary dark:text-dark-text-high text-sm font-bold">
                      {org.name?.charAt(0) || '?'}
                    </div>
                  {/if}
                  <span class="text-body1 font-medium text-content">{org.name}</span>
                </div>
              </td>
              <td class="px-6 py-4 text-body2 text-content-secondary font-mono">{org.domain}</td>
              <td class="px-6 py-4">
                <span class={`badge ${getTypeBadge(org.org_type)}`}>{getTypeLabel(org.org_type)}</span>
              </td>
              <td class="px-6 py-4 text-center text-body2 text-content-tertiary">
                {org.employee_count ? org.employee_count.toLocaleString() : '-'}
              </td>
              <td class="px-6 py-4 text-center">
                {#if org.auto_verify}
                  <span class="text-success font-medium">✓</span>
                {:else}
                  <span class="text-gray-400">-</span>
                {/if}
              </td>
              <td class="px-6 py-4 text-center">
                {#if org.verification_enabled}
                  <span class="badge badge-success">사용</span>
                {:else}
                  <span class="badge badge-neutral">미사용</span>
                {/if}
              </td>
              <td class="px-6 py-4 text-center">
                {#if org.login_allowed}
                  <span class="badge badge-success">허용</span>
                {:else}
                  <span class="badge badge-error">제한</span>
                {/if}
              </td>
              <td class="px-6 py-4 text-body2 text-content-tertiary">
                {new Date(org.created_at).toLocaleDateString('ko-KR')}
              </td>
              <td class="px-6 py-4 text-right">
                <div class="flex items-center justify-end gap-2">
                  <a href={`/admin/organizations/${org.id}`} class="text-primary hover:text-primary-dark text-body2 font-medium">상세</a>
                  <a href={`/admin/organizations/${org.id}/edit`} class="text-gray-500 hover:text-gray-700 dark:text-dark-text-low dark:hover:text-dark-text-high text-body2">수정</a>
                  <button
                    type="button"
                    onclick={() => toggleVerification(org)}
                    class="text-body2 text-info hover:text-primary transition-colors"
                  >
                    인증 {org.verification_enabled ? '끄기' : '켜기'}
                  </button>
                  <button
                    type="button"
                    onclick={() => toggleLoginAccess(org)}
                    class={`text-body2 transition-colors ${org.login_allowed ? 'text-error hover:text-error-dark' : 'text-success hover:text-success-dark'}`}
                  >
                    이용 {org.login_allowed ? '제한' : '허용'}
                  </button>
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>

      {#if organizations.length === 0}
        <div class="text-center py-12 text-content-tertiary">
          등록된 기관이 없습니다
        </div>
      {/if}
    </div>

    <!-- Pagination -->
    {#if pagination.total_pages > 1}
      <div class="flex items-center justify-center gap-2 mt-6">
        <button
          onclick={() => goToPage(pagination.current_page - 1)}
          disabled={pagination.current_page === 1}
          class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >이전</button>
        <span class="px-4 py-2 text-body2 text-content-tertiary">
          {pagination.current_page} / {pagination.total_pages}
        </span>
        <button
          onclick={() => goToPage(pagination.current_page + 1)}
          disabled={pagination.current_page === pagination.total_pages}
          class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >다음</button>
      </div>
    {/if}
  </main>
